import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {Modal} from "../../system/modal";
import SelectInput from "../../system/select";
import {MoneyInput} from "../../system/MoneyInput";
import {getPersonsNameAndFamily} from "../../../services/profile/profile";
import {createManualPayment, getPaymentDestinations} from "../../../services/admin_service/manual_payment";
import {MANUAL_PAYMENT_ATTEMPT_KEY, MANUAL_PAYMENT_STATUSES, manualPaymentPayload, submissionAttempt} from "./manualPaymentModel";

import {ManualPaymentDialogHeading, ManualPaymentNotice} from "./ManualPaymentUI";

const emptyForm = {personId: null, destinationAccountId: "", amount: "", reference: "", description: ""};
const inputClass = "manual-payment-field";

function storedAttempt() {
  try { return JSON.parse(sessionStorage.getItem(MANUAL_PAYMENT_ATTEMPT_KEY)); } catch { return null; }
}

export function ManualPaymentForm({open, onClose, onCreated}) {
  const [form, setForm] = useState(emptyForm);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revision, setRevision] = useState(0);
  const attempt = useRef(storedAttempt());
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setLoadError("");
    getPaymentDestinations({signal: controller.signal}).then(result => {
      if (active) setDestinations(result.items || []);
    }).catch(cause => {
      if (active && cause?.code !== "ERR_CANCELED") setLoadError(cause.message || "دریافت حساب‌های مقصد انجام نشد.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [open, revision]);

  const change = (field, value) => {
    setForm(current => ({...current, [field]: value}));
    setError("");
  };

  const submit = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    setError("");
    let payload;
    try {
      payload = manualPaymentPayload(form);
      if (!destinations.some(destination => destination.id === payload.destination_account_id)) throw new Error("حساب مقصد در دسترس نیست.");
      attempt.current = submissionAttempt(payload, attempt.current);
      // Preserve an uncertain attempt across closing the dialog or reloading.
      // Only the normalized command and its retry key are kept in this tab.
      try { sessionStorage.setItem(MANUAL_PAYMENT_ATTEMPT_KEY, JSON.stringify(attempt.current)); } catch { /* The in-memory attempt still supports retries. */ }
    } catch (cause) {
      setError(cause.message);
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    try {
      const result = await createManualPayment(payload, attempt.current.key);
      try { sessionStorage.removeItem(MANUAL_PAYMENT_ATTEMPT_KEY); } catch { /* Storage may be unavailable. */ }
      attempt.current = null;
      setForm(emptyForm);
      const status = MANUAL_PAYMENT_STATUSES[result.payment?.status]?.label || "ثبت‌شده";
      toast.success(result.created ? "واریزی ثبت شد و در انتظار تایید ادمین است." : `این واریزی قبلاً ثبت شده است؛ وضعیت فعلی: ${status}`);
      onCreated(result.payment);
    } catch (cause) {
      setError(cause.message || "ثبت واریزی انجام نشد. دوباره تلاش کنید.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} setOpen={() => { if (!inFlight.current) onClose(); }} size="md" hideFooter>
      {{
        header: <ManualPaymentDialogHeading title="ثبت واریزی کارت به کارت" subtitle="اطلاعات واریزی مشتری را برای بررسی ادمین ثبت کنید." onClose={onClose} disabled={submitting}/>,
        body: (
          <form onSubmit={submit} className="manual-payment-dialog" dir="rtl">
            <ManualPaymentNotice>وضعیت اولیه «در انتظار تایید» است. مبلغ فقط پس از تایید ادمین به موجودی مشتری اضافه می‌شود.</ManualPaymentNotice>
            <div className="manual-payment-form-grid">
              <div className="manual-payment-form-wide">
                <SelectInput
                  label="کاربر" defaultValue={form.personId} refreshSignal={form.personId}
                  autoLoadService={getPersonsNameAndFamily}
                  autoLoadRender={({name, family, phone}) => `${name || ""} ${family || ""} — ${phone || ""}`.trim()}
                  onChange={value => change("personId", value)} disabled={submitting} stackedLabel
                />
              </div>
              <label className="manual-payment-label">مبلغ (ریال)
                <MoneyInput className={`${inputClass} text-right`} value={form.amount} placeholder="مثلاً ۱,۰۰۰,۰۰۰" onValueChange={value => change("amount", value)} disabled={submitting} required/>
              </label>
              <label className="manual-payment-label">نوع تراکنش
                <select className={inputClass} value="card_to_card" disabled><option value="card_to_card">کارت به کارت</option></select>
              </label>
              <label className="manual-payment-label manual-payment-form-wide">روش پرداخت (حساب مقصد)
                <select className={inputClass} value={form.destinationAccountId} onChange={event => change("destinationAccountId", event.target.value)} disabled={loading || submitting || Boolean(loadError)} required>
                  <option value="">{loading ? "در حال دریافت حساب‌ها…" : "انتخاب حساب مقصد"}</option>
                  {destinations.map(destination => <option value={destination.id} key={destination.id}>{destination.title} — {destination.bank_name}</option>)}
                </select>
              </label>
              {loadError ? <div role="alert" className="manual-payment-error manual-payment-form-wide">{loadError} <button type="button" onClick={() => setRevision(value => value + 1)} className="underline">تلاش دوباره</button></div> : !loading && destinations.length === 0 ? <p role="alert" className="manual-payment-error manual-payment-form-wide">حساب مقصد فعالی برای ثبت واریزی تنظیم نشده است.</p> : null}
              <label className="manual-payment-label manual-payment-form-wide">شماره رسید یا مرجع (اختیاری)
                <input className={inputClass} value={form.reference} maxLength={100} placeholder="شماره پیگیری درج‌شده در رسید بانکی" onChange={event => change("reference", event.target.value)} disabled={submitting}/>
              </label>
              <label className="manual-payment-label manual-payment-form-wide">توضیحات (اختیاری)
                <textarea className={inputClass} value={form.description} maxLength={4000} placeholder="اطلاعات تکمیلی برای بررسی واریزی" onChange={event => change("description", event.target.value)} disabled={submitting}/>
              </label>
            </div>
            <p className="manual-payment-help">عامل و تاریخ و زمان ثبت، به‌صورت خودکار ثبت می‌شوند.</p>
            {error && <p role="alert" className="manual-payment-error">{error}</p>}
            <div className="manual-payment-dialog-actions">
              <button type="button" onClick={onClose} disabled={submitting} className="manual-payment-button">انصراف</button>
              <button type="submit" disabled={submitting || loading || Boolean(loadError) || destinations.length === 0} className="manual-payment-button is-primary">{submitting ? "در حال ثبت…" : "ثبت و ارسال برای تایید"}</button>
            </div>
          </form>
        ),
      }}
    </Modal>
  );
}
